/**
 * チュートリアルツアーの設定
 *
 * 各ツアーは以下の構造を持ちます：
 * - key: ツアーの一意な識別子
 * - steps: ツアーのステップ配列
 *   - title: ステップのタイトル
 *   - description: ステップの説明
 *   - target: ハイライトする要素（関数または null）
 *   - placement: ツールチップの表示位置
 *   - tabKey: (オプション) このステップで表示すべきタブのkey
 */

export const TOUR_CONFIGS = {
  // ===================================================================
  // ダッシュボード
  // ===================================================================
  dashboard: {
    key: 'dashboard',
    steps: [
      {
        title: 'magicssへようこそ！',
        description:
          'ここはAIOコンテンツモードのダッシュボードです。ランク進捗、統計情報、最近のリサーチ結果などを一覧できます。',
        target: null,
      },
      {
        title: 'ランク進捗',
        description:
          '左側には、あなたの現在のランクとEXP、次のランク特典が表示されます。ログインやリサーチ、記事生成でEXPを獲得し、ランクアップすると様々な特典が解放されます。',
        target: () =>
          document.querySelector('.rank-progress-card') ||
          document.querySelector('.ant-card'),
        placement: 'right',
      },
      {
        title: '統計情報',
        description:
          '右側には、累計のログイン数、リサーチ数、記事生成数などの統計が表示されます。使えば使うほど便利になる仕組みです。',
        target: () =>
          document.querySelectorAll('.ant-col')[1] ||
          document.querySelectorAll('.ant-card')[1],
        placement: 'left',
      },
      {
        title: '最近のリサーチ',
        description:
          '実行したリサーチの履歴が表示されます。リサーチ結果を元に記事を生成できます。',
        target: () => document.querySelector('.recent-research-card'),
        placement: 'top',
      },
      {
        title: '最近の記事生成',
        description:
          '生成した記事の履歴が表示されます。ここから記事の編集や管理ができます。',
        target: () => document.querySelector('.recent-generation-card'),
        placement: 'top',
      },
      {
        title: '新規リサーチを始めましょう',
        description:
          '早速、新しいリサーチを開始してみましょう。サイドバーの「新規リサーチ」をクリックしてください。',
        target: () =>
          document.querySelector('[href="/aio/research"]') ||
          document.querySelector('.ant-menu-item'),
        placement: 'right',
      },
    ],
  },

  // ===================================================================
  // リサーチ入力画面
  // ===================================================================
  research_input: {
    key: 'research_input',
    steps: [
      {
        title: 'リサーチ機能について',
        description:
          'SEO/AIOに強いコンテンツを作る第一歩は、「検索者が何を求めているか」をとにかく深く理解することです。この機能ではAIが5分程度で検索者や競合サイトの情報を徹底分析し、レポート化します。',
        target: () => null,
      },
      {
        title: 'リサーチと生成の流れ',
        description:
          'リサーチした結果を元に、AIと共に記事の構成を考え、記事を生成していく構造になっています。（リサーチなしでも生成はできますが、精度や使える機能が劣ります）',
        target: () =>
          document.querySelector('.generation-flow-card') ||
          document.querySelector('.generation-flow-card .ant-card-body'),
        placement: 'top',
      },
      {
        title: 'キーワードを入力',
        description:
          'まずは、リサーチしたい検索キーワードを入力します。例：「東京 カフェ おすすめ」「プログラミング 初心者 学習方法」',
        target: () => document.querySelector('.ant-select-selection-search'),
        placement: 'bottom',
      },
      {
        title: 'お気に入り登録',
        description:
          'リサーチ結果で見つけたキーワードは★マークでお気に入り登録でき、こちらに表示されます。',
        target: () =>
          document.querySelector('[class*="favorite"]') ||
          document.querySelectorAll('.ant-card')[1],
        placement: 'left',
      },
      {
        title: 'リサーチを実行',
        description:
          '早速「狙いたいキーワード」を入力して、新規リサーチを実行してみましょう！今回はチュートリアルでEXPを多めに獲得でき、ランク特典でボーナスクレジットが付与されます。',
        target: () =>
          document.querySelector('button[type="primary"]') ||
          document.querySelector('.start-analysis-button'),
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // リサーチ結果画面
  // ===================================================================
  research_result: {
    key: 'research_result',
    steps: [
      {
        title: 'リサーチ結果が完成しました！',
        description:
          'AIエージェントが検索結果の上位サイト、キーワード、トピックなど膨大なデータを分析し、SEO/AIOに強いコンテンツ作成のための分析レポートを作成しました。主要項目の活用方法を順番に見ていきましょう。',
        target: null,
        tabKey: '1',
      },
      {
        title: 'タブの切り替え',
        description:
          '画面上部の4つのタブで分析結果を切り替えます。①分析サマリー、②競合サイト分析、③AI Overviews（検索結果にAIOがある場合のみ）、④コンテンツ戦略。それぞれ異なる視点で分析されています。',
        target: () => document.querySelector('.ant-tabs-nav'),
        placement: 'bottom',
        tabKey: '1',
      },
      {
        title: '検索結果の傾向（SERP分析）',
        description:
          '上位表示サイトのタイプ（企業サイト、個人ブログ、メディアなど）やコンテンツ内容、競合サイトの平均的な文字数やSEO難易度などの全体像が把握できます。',
        target: () => {
          const cards = document.querySelectorAll('.v2-card');
          for (let card of cards) {
            const title = card.querySelector('.ant-card-head-title');
            if (
              title &&
              (title.textContent.includes('SERP') ||
                title.textContent.includes('検索結果'))
            ) {
              return card;
            }
          }
          return cards[0];
        },
        placement: 'bottom',
        tabKey: '1',
      },
      {
        title: '検索意図の分析',
        description:
          '現在のSEOにおいて最も重要な「検索意図」です。検索者が何を求めているのか、どんなニーズがあるのかを深く細かく理解することが、良質なコンテンツの第一歩です。',
        target: () => {
          const cards = document.querySelectorAll('.v2-card');
          for (let card of cards) {
            const title = card.querySelector('.ant-card-head-title');
            if (title && title.textContent.includes('検索意図')) {
              return card;
            }
          }
          return cards[1];
        },
        placement: 'bottom',
        tabKey: '1',
      },
      {
        title: 'トピック構造マップ',
        description:
          '上位表示しているサイトで扱われているトピック（情報の内容）を整理した図です。○が大きいトピックほど、多くのサイトで重点的に扱われていることを意味します。左上のバーで絞り込みもできます。',
        target: () => document.querySelector('.tour-topic-sunburst-section'),
        placement: 'bottom',
        tabKey: '1',
      },
      {
        title: '関連キーワードやよくある質問',
        description:
          'GoogleサジェストやLSIキーワード、そして「他の人はこちらも質問」等の内容が表示されます。気になったキーワードをクリックするとお気に入り登録できます。',
        target: () => {
          const cards = document.querySelectorAll('.v2-card');
          for (let card of cards) {
            const title = card.querySelector('.ant-card-head-title');
            if (title && title.textContent.includes('関連キーワード')) {
              return card;
            }
          }
          return cards[3];
        },
        placement: 'bottom',
        tabKey: '1',
      },
      {
        title: '競合サイト分析タブに移動',
        description:
          '次は、上位1位～10位のサイトを詳しく分析した結果を見ていきましょう。タブを切り替えました。',
        target: () => document.querySelectorAll('.ant-tabs-tab')[1],
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'top-sites',
      },
      {
        title: '【上位サイト】競合サイト一覧',
        description:
          '検索結果の1位～10位程度のページが表示されます。タイトル、URL、文字数、ドメイン評価などの基本情報に加え、「強み（Strength）」スコアで各サイトの競合力を数値化しています。',
        target: () => document.querySelector('.tour-competitor-list-section'),
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'top-sites',
      },
      {
        title: '【上位サイト】各項目の見方',
        description:
          '雷マークの数値はStrengthスコアです。ドメインの信頼性やジャンルとの関連性（専門性）等を総合的にAIが数値化したもので、競合サイトの強さの傾向を計るための参考数値となります。',
        target: () => document.querySelector('.tour-first-competitor-card'),
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'top-sites',
      },
      {
        title: '【見出し構造】サブタブに切り替え',
        description:
          '次は、各サイトの見出し構造（H2、H3、H4...）を見ていきます。どんなトピックを、どの順番で、どの深さで扱っているかが一目瞭然です。',
        target: () => {
          const radioGroup = document.querySelector('.ant-radio-group');
          if (radioGroup) {
            const buttons = radioGroup.querySelectorAll(
              '.ant-radio-button-wrapper',
            );
            for (let btn of buttons) {
              if (btn.textContent.includes('見出し')) {
                return btn;
              }
            }
            return buttons[1];
          }
          return document.querySelector('.ant-radio-group');
        },
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'headings',
      },
      {
        title: '【トピック】サブタブに切り替え',
        description:
          'トピック分析では、各サイトがどのトピックをどの程度扱っているかを数値化しています。',
        target: () => document.querySelectorAll('.ant-radio-button-wrapper')[2],
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'topics',
      },
      {
        title: '【トピック】ヒートマップの見方',
        description:
          '表の数値は「トピック言及の強度スコア」です。縦軸がトピック、横軸が1～10位の各サイト。数値が高いほど、そのサイトがそのトピックを重視していることを示します。多くのサイトで高スコアのトピックは「必須トピック」、自分だけ高スコアにすれば「差別化ポイント」になります。',
        target: () => document.querySelector('.tour-topics-heatmap'),
        placement: 'top',
        tabKey: '2',
        subTabKey: 'topics',
      },
      {
        title: '【重要語句】【キーワード】',
        description:
          '同様に、上位サイトで頻出する重要語句や関連キーワード、LSIキーワードもヒートマップ表で表示されます。',
        target: () => document.querySelectorAll('.ant-radio-button-wrapper')[3],
        placement: 'bottom',
        tabKey: '2',
        subTabKey: 'terms',
      },
      {
        title: 'AI Overviewsタブへ',
        description:
          'Google検索に表示される「AI Overview」の分析結果を見てみましょう。このキーワードでAI要約が表示される場合のみ、このタブが有効になります。',
        target: () => document.querySelectorAll('.ant-tabs-tab')[2],
        placement: 'bottom',
        tabKey: '3',
      },
      {
        title: 'AI Overviewの内容',
        description:
          'Googleが生成したAI要約の内容が表示されます。どんな情報を提示しているか、どのサイトが引用されているかを確認しましょう。',
        target: () => {
          // タブ3の内容全体を取得
          const tabs = document.querySelectorAll('.ant-tabs-tabpane');
          for (let tab of tabs) {
            if (
              tab.getAttribute('data-node-key') === '3' ||
              tab.id?.includes('3')
            ) {
              // タブ内の最初のカード、またはタブ全体
              const card = tab.querySelector('.v2-card');
              if (card) return card;
              return tab;
            }
          }
          // フォールバック
          const allCards = document.querySelectorAll('.v2-card');
          return allCards[0] || document.querySelector('.ant-card');
        },
        placement: 'bottom',
        tabKey: '3',
      },
      {
        title: 'コンテンツ戦略タブへ',
        description: '最後に、AIが提案する「コンテンツ戦略」を確認しましょう。',
        target: () => document.querySelectorAll('.ant-tabs-tab')[3],
        placement: 'bottom',
        tabKey: '4',
      },
      {
        title: '基本戦略・トピック選定方針',
        description:
          '検索意図や上位サイトのコンテンツ傾向などを踏まえた上で、どのような情報を書くべきか、差別化のための道筋はどのようなものかをAIがまとめています。',
        target: () => document.querySelector('.tour-strategy-basic'),
        placement: 'bottom',
        tabKey: '4',
      },
      {
        title: '【E-E-A-T】信頼性を高める',
        description:
          'Googleが重視する「経験（Experience）・専門性（Expertise）・権威性（Authoritativeness）・信頼性（Trustworthiness）」を高めるための具体的な提案。どんな独自データを入れるべきか、どう信頼性を担保するか、実体験をどう活かすかのヒントが示されます。',
        target: () => document.querySelector('.tour-strategy-eeat'),
        placement: 'bottom',
        tabKey: '4',
      },
      {
        title: '【別記事アイデア】コンテンツを拡張',
        description:
          '1記事で全てを語るのではなく、関連トピックを別記事に分けることで、サイト全体の専門性とSEO評価を高める戦略です。どんなテーマで記事を増やすべきか、内部リンクでどう繋げるかのアイデアが提示されます。',
        target: () => document.querySelector('.tour-strategy-subqueries'),
        placement: 'bottom',
        tabKey: '4',
      },
      {
        title: '【読者満足度】ユーザー体験の最大化',
        description:
          '検索者が「この記事で解決できた」「読んでよかった」と感じるためのポイント。読みやすさ、情報の整理、ビジュアル要素、実用性など、ユーザー体験を最大化するための提案が示されます。SEOだけでなく、実際の読者満足度を高めることが、結果的にSEO評価にも繋がります。',
        target: () => document.querySelector('.tour-strategy-uniqueness'),
        placement: 'bottom',
        tabKey: '4',
      },
      {
        title: 'まとめ：次のステップへ',
        description:
          '以上が基本的なリサーチ結果の見方です！次はメニューから「新規生成」をクリックして、このリサーチデータを活用した記事の作成に進みましょう。',
        target: () => {
          const buttons = document.querySelectorAll('button');
          for (let btn of buttons) {
            if (
              btn.textContent.includes('記事生成') ||
              btn.textContent.includes('STEP2')
            ) {
              return btn;
            }
          }
          return document.querySelector('button[type="primary"]');
        },
        placement: 'top',
        tabKey: '1',
      },
    ],
  },

  // ===================================================================
  // 生成STEP1: 基本設計
  // ===================================================================
  generation_step1: {
    key: 'generation_step1',
    steps: [
      {
        title: 'STEP1: 記事生成の基本設計',
        description:
          'まずはリサーチ結果、スタイルなどを選択して、記事の方向性を決定します。プロのSEOディレクターやライターの業務フローを、AIと共に進めましょう。',
        target: null,
      },
      {
        title: 'リサーチ結果を選択（重要）',
        description:
          'リサーチ結果を活用することで、検索意図やトピック等のデータを活用して記事を生成できます。リサーチ結果なしでも生成はできますが、品質や機能に大きな違いがあります。リサーチ結果がある場合は必ず選択しましょう。',
        target: () => document.querySelector('.ant-select'),
        placement: 'bottom',
      },
      {
        title: 'ターゲットキーワード',
        description:
          'この記事で狙うメインキーワードです。リサーチ結果を選択すると自動で入力されます。',
        target: () => document.querySelector('input[placeholder*="例：SEO"]'),
        placement: 'bottom',
      },
      {
        title: 'スタイルを選ぶ',
        description:
          '記事の文体や構成のスタイルを選びます。「デフォルト」は説明的で体系的な標準スタイルです。ランクが上がると、独自スタイルを登録して使えるようになります。',
        target: () =>
          document.querySelector('.ant-card .ant-select') ||
          document.querySelector('.ant-card'),
        placement: 'bottom',
      },
      {
        title: 'スタイル情報の確認',
        description:
          '選択したスタイルの特徴（文体トーン、専門性、箇条書き頻度など）が表示されます。AIがこのスタイルを一つの基準として記事を生成していきます。',
        target: () =>
          document.querySelector('.style-info-panel') ||
          document.querySelector('.ant-card .ant-card-body'),
        placement: 'left',
      },
      {
        title: 'スタイルの微調整（上級機能）',
        description:
          'スタンダードプラン以上で利用可能な機能です。この記事だけに適用するスタイルの微調整ができます。見出しの数、装飾の頻度、カスタムルールなどを細かく設定可能です。',
        target: () => {
          const buttons = document.querySelectorAll('button');
          for (let btn of buttons) {
            if (btn.textContent.includes('微調整')) {
              return btn;
            }
          }
          return null;
        },
        placement: 'top',
      },
      {
        title: '次へボタンで進む',
        description:
          '設定が完了したら「次へ」ボタンでSTEP2に進みます。この段階でベースクレジットとして30クレジットが消費され、STEP4の本文生成のタイミングで従量クレジットが消費されます。',
        target: () => {
          const buttons = document.querySelectorAll('button[type="primary"]');
          // 最後のprimaryボタン（次へボタン）を取得
          return buttons[buttons.length - 1];
        },
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP2: ベースアウトライン選択（3案から選択）
  // ===================================================================
  generation_step2: {
    key: 'generation_step2',
    steps: [
      {
        title: 'STEP2: 構成パターンの選択',
        description:
          'AIがリサーチ結果を分析し、3つの異なる切り口で構成案を生成しました。「情報網羅型」「深掘り型」「共感型」から、記事の方向性に合うパターンを選びましょう。',
        target: null,
      },
      {
        title: 'パターンタブで切り替え',
        description:
          '上部のタブで構成パターンを切り替えられます。「情報網羅型」は幅広い情報をカバー、「深掘り型」は特定テーマを深く掘り下げ、「共感型」は読者に寄り添う構成です。',
        target_key: 'outline_grid',
        placement: 'bottom',
      },
      {
        title: '見出し構成',
        description:
          '左側には選択中のパターンの見出し構成が表示されます。オレンジのH2タグは大見出し、黄色のH3タグは中見出しです。右上の「展開」スイッチで各見出しの詳細を表示できます。',
        target_key: 'outline_sections',
        placement: 'right',
      },
      {
        title: '構成サマリー',
        description:
          '右上には「H2+H3見出し数」と「想定文字数」が表示されます。これはあくまで目安で、次のステップで構成を編集したり、スタイル設定で文字数を調整できます。',
        target_key: 'outline_stats',
        placement: 'left',
      },
      {
        title: '構成の戦略',
        description:
          'この構成パターンの「戦略反映」「差別化ポイント」「読者価値」が示されます。どのような狙いでこの構成になっているかを理解し、編集時の参考にしましょう。',
        target_key: 'outline_rationale',
        placement: 'left',
      },
      {
        title: '参考情報リスト',
        description:
          'リサーチ結果からの参考情報が表示されます。見出しの横に付いた数字をクリックすると、対応する参考情報がハイライトされます。この情報は本文生成時に活用されます。',
        target_key: 'reference_list',
        placement: 'left',
      },
      {
        title: 'この構成案で編集に進む',
        description:
          'パターンを選んだら、このボタンでSTEP3に進みます。次のステップでは見出しの追加・削除・並び替えなど、自由に編集できます。',
        target_key: 'confirm_button',
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP3: 構成案編集
  // ===================================================================
  generation_step3: {
    key: 'generation_step3',
    steps: [
      {
        title: 'STEP3: 構成案の編集',
        description:
          '記事の骨子を固める重要なステップです。AIが生成した構成案を編集・調整し、リサーチ結果と照らし合わせながら最適な構成を作り上げます。',
        target_key: null,
        target: null,
      },
      {
        title: '記事タイトル（H1）',
        description:
          '記事のタイトルを編集できます。後の本文生成にも影響するため、魅力的且つ内容を分かりやすく示すタイトルにしましょう。',
        target_key: 'article_title',
        target: () => document.querySelector('.tour-article-title'),
        placement: 'bottom',
      },
      {
        title: '構成案（H2/H3リスト）',
        description:
          'ここに記事の見出し構成が表示されています。左側のアイコンをドラッグ＆ドロップで並び替え、右側の「…」アイコンから追加・複製・削除・メモ編集ができます。H3/H4のラベルをクリックすると見出しレベルを切り替えられます。',
        target_key: 'sections_list',
        target: () => document.querySelector('.tour-sections-list'),
        placement: 'right',
      },
      {
        title: '編集モード切り替え',
        description:
          '「パズル」モードでドラッグ＆ドロップでの並び替え、「テキスト」モードでテキスト形式での編集が可能です。お好みの方法で構成案を編集しましょう。',
        target_key: 'edit_mode_toggle',
        target: () => document.querySelector('.tour-edit-mode-toggle'),
        placement: 'bottom',
      },
      {
        title: '一時保存',
        description:
          '編集内容を一時保存すると、作業を中断して後で再開できます。こまめに保存しましょう。',
        target_key: 'temp_save_button',
        target: () => document.querySelector('.tour-temp-save-button'),
        placement: 'bottom',
      },
      {
        title: '右カラム: トピックリスト',
        description:
          'リサーチで形成されたトピックがカテゴリーごとに表示されます。スコア範囲で絞り込みもできます。独自情報タブ（スタンダードプラン以上で利用可能）では、自社の商品情報やデータを記事に組み込めます。',
        target_key: 'right_column_panel',
        target: () => document.querySelector('.tour-right-column-panel'),
        placement: 'left',
      },
      {
        title: 'スコアリング結果',
        description:
          'リサーチ結果を紐づけた場合、AIが構成案を評価した「カバレッジスコア」と「ユニークネススコア」が表示されます。「分析画面を開く」ボタンで、詳細を確認できます。',
        target_key: 'score_section',
        target: () => document.querySelector('.tour-score-section'),
        placement: 'left',
      },
      {
        title: '詳細な分析を確認',
        description:
          '分析画面では、スコアの内訳や総評、改善アイデア、競合との見出し比較、リサーチ結果のデータを確認できます。ブラッシュアップに活用しましょう。画面左下のボタンから再スコアリングもできます。',
        target_key: 'analysis_modal',
        target: null,
      },
      {
        title: '構成案を確定して次へ',
        description:
          '編集が完了したら、画面下部の「構成を確定して本文へ」ボタンで構成案を確定し、次のステップに進みます。',
        target_key: 'confirm_next_button',
        target: () => document.querySelector('.tour-confirm-next-button'),
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP4.1: 執筆プラン
  // ===================================================================
  generation_step4_1: {
    key: 'generation_step4_1',
    steps: [
      {
        title: '執筆プランの確認と編集',
        description:
          'いよいよ本文生成のステップです。まずは各見出しごとに「何を書くか」を明確にした執筆プランをAIが自動生成します。',
        target_key: null,
        target: null,
      },
      {
        title: 'STEP4「本文生成」のフロー',
        description:
          'STEP4は「執筆プラン → 本文生成 → ブラッシュアップ → 最終調整」の4段階で進みます。各ステップで記事の品質を段階的に高めていきます。',
        target_key: 'substeps',
        target: () => document.querySelector('.tour-step4-substeps'),
        placement: 'bottom',
      },
      {
        title: '構成案の見出し一覧',
        description:
          'STEP3で作成した構成案が表示されます。各見出しをクリックすると、右側に対応する執筆プランが表示されます。',
        target_key: 'outline_list',
        target: () => document.querySelector('.outline-list-step4'),
        placement: 'right',
      },
      {
        title: '執筆プランの詳細',
        description:
          '選択した見出しの執筆プランが表示されます。AIが生成した「セクションのゴール」「アウトライン」「補足」を確認し、必要に応じて編集できます。各見出しごとに、何をどう書くかを具体化していきましょう。',
        target_key: 'section_detail',
        target: () => document.querySelector('.section-detail-form'),
        placement: 'left',
      },
      {
        title: '統計情報と一時保存',
        description:
          '上部には構成案の統計情報（H2/H3の個数、想定文字数）が表示されます。「一時保存する」ボタンで、執筆プランの編集内容を保存できます。',
        target_key: 'stats_and_save',
        target: () =>
          document.querySelector('.outline-list-step4')?.previousElementSibling,
        placement: 'bottom',
      },
      {
        title: '従量クレジットの確認',
        description:
          '本文生成には構成案の規模に応じた従量クレジットが消費されます。生成前に必要なクレジット数を確認しましょう。',
        target_key: 'credit_info',
        target: () => {
          // 確定ボタンの直前の兄弟要素を探す（クレジット情報ボックス）
          const buttons = Array.from(document.querySelectorAll('button'));
          const confirmButton = buttons.find(btn =>
            btn.textContent.includes('執筆プラン'),
          );
          if (confirmButton && confirmButton.previousElementSibling) {
            return confirmButton.previousElementSibling;
          }
          return null;
        },
        placement: 'top',
      },
      {
        title: '執筆プランを確定して本文生成へ',
        description:
          '執筆プランの確認・編集が完了したら、このボタンで確定し、本文生成を開始します。生成には数分かかります。',
        target_key: 'confirm_button',
        target: () => {
          // 「執筆プランを確定」テキストを含むボタンを探す
          const buttons = Array.from(document.querySelectorAll('button'));
          const confirmButton = buttons.find(btn =>
            btn.textContent.includes('執筆プラン'),
          );
          return confirmButton;
        },
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP4.2: 本文生成
  // ===================================================================
  generation_step4_2: {
    key: 'generation_step4_2',
    steps: [
      {
        title: '本文生成',
        description:
          '執筆プランに基づき、いよいよAIが本文を生成します。エージェントが情報を深く集めながらリアルタイムで執筆が進みます。画面を閉じても生成は継続されるので、数分お待ちください。',
        target_key: null,
        target: null,
      },
      {
        title: 'サブステップナビゲーション',
        description:
          '現在はSTEP4.2「本文生成」です。生成完了後、STEP4.3「ブラッシュアップ」に進めます。',
        target_key: 'substeps',
        target: () => document.querySelector('.tour-step4-substeps'),
        placement: 'bottom',
      },
      {
        title: '生成進捗バー',
        description:
          '生成中は進捗状況が表示されます。複数のタブを開いている場合は、リアルタイムで更新されない可能性があります。',
        target_key: 'progress_bar',
        target: () => {
          // 生成中の進捗カードを探す（「X / Y 見出し」を含むカード）
          const cards = Array.from(document.querySelectorAll('.ant-card'));
          const progressCard = cards.find(
            card =>
              card.textContent.includes('見出し') &&
              card.querySelector('.ant-progress'),
          );
          return progressCard;
        },
        placement: 'bottom',
      },
      {
        title: '統計情報',
        description:
          '生成が進むにつれて、総見出し数、成功・失敗数、総文字数などの統計情報が更新されます。',
        target_key: 'statistics',
        target: () => {
          // 統計情報カードを探す（「総見出し数」を含むカード）
          const cards = Array.from(document.querySelectorAll('.ant-card'));
          const statsCard = cards.find(card =>
            card.textContent.includes('総見出し数'),
          );
          return statsCard;
        },
        placement: 'bottom',
      },
      {
        title: '生成された記事本文',
        description:
          '生成された本文が表示されます。引用番号をクリックすると、右側の参考データがハイライトされます。',
        target_key: 'article_content',
        target: () => {
          // padding: 24pxのdiv内で最初のRowを探し、その中の.ant-col-16を返す
          const mainContainer =
            document.querySelector('div[style*="padding: 24px"]') ||
            document.querySelector('div[style*="padding:24px"]');
          if (mainContainer) {
            const row = mainContainer.querySelector('.ant-row');
            if (row) {
              return row.querySelector('.ant-col-16');
            }
          }
          return null;
        },
        placement: 'right',
      },
      {
        title: '目次と参考データの切り替え',
        description:
          '「目次」と「参考データ」を切り替えられます。目次では現在の読み位置がハイライトされ、クリックでジャンプできます。参考データでは、本文中の引用番号に対応するエビデンスが確認できます。',
        target_key: 'right_panel_toggle',
        target: () => {
          // Segmentedコンポーネント（目次/参考データの切り替え）
          return document.querySelector('.ant-segmented');
        },
        placement: 'left',
      },
      {
        title: '内容表示エリア',
        description:
          '目次モードでは記事の構造を一覧でき、参考データモードでは引用元の詳細情報を確認できます。本文中の引用番号をクリックすると、自動的に参考データモードに切り替わり、該当箇所がハイライトされます。',
        target_key: 'right_panel_content',
        target: () => {
          // Segmentedの下のスクロール可能なコンテンツエリアを探す
          const segmented = document.querySelector('.ant-segmented');
          if (segmented) {
            // Segmentedの親要素から、スクロール可能な領域を探す
            const parent = segmented.closest('div[style*="display: flex"]');
            if (parent) {
              // flex: 1, overflowY: auto の子要素を探す
              const scrollableDiv = parent.querySelector(
                'div[style*="flex: 1"]',
              );
              return scrollableDiv;
            }
          }
          return null;
        },
        placement: 'left',
      },
      {
        title: 'ブラッシュアップへ進む',
        description:
          '本文生成が完了したら、このボタンでSTEP4.3（ブラッシュアップ）に進みます。AIが記事の品質をさらに高める改善提案を行います。',
        target_key: 'next_button',
        target: () => {
          // 「ブラッシュアップへ進む」ボタンを探す（全ボタンから検索）
          const buttons = Array.from(document.querySelectorAll('button'));
          const nextButton = buttons.find(btn =>
            btn.textContent.includes('ブラッシュアップ'),
          );
          return nextButton;
        },
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP4: 本文執筆（以前の執筆プラン）
  // ===================================================================
  generation_step4: {
    key: 'generation_step4',
    steps: [
      {
        title: '執筆プランの作成',
        description:
          '各見出しで「何を書くべきか」の執筆プランを作成します。これにより、本文生成の精度が大幅に向上します。',
        target: null,
      },
      {
        title: '見出しの選択',
        description:
          '左側に記事の見出し一覧が表示されます。執筆プランを確認・編集したい見出しをクリックしてください。',
        target: () =>
          document.querySelector('.heading-list') ||
          document.querySelector('.ant-menu'),
        placement: 'right',
      },
      {
        title: '執筆プランの生成',
        description:
          '「執筆プランを生成」ボタンをクリックすると、各見出しの執筆方針がAIで自動生成されます。必須ではありませんが、より精度の高い本文を生成できます。',
        target: () =>
          document.querySelector('.generate-writing-plan-button') ||
          document.querySelector('button:has-text("執筆プラン")'),
        placement: 'left',
      },
      {
        title: '執筆プランの内容',
        description:
          '生成された執筆プランを確認し、必要に応じて編集できます。各見出しで何を書くべきかの指針が示されます。',
        target: () => document.querySelector('.writing-plan-display'),
        placement: 'left',
      },
      {
        title: 'クレジット消費のタイミング',
        description:
          '本文生成を開始した時点でクレジットが消費されます。事前に確認しておきましょう。',
        target: () =>
          document.querySelector('.credit-warning') ||
          document.querySelector('[class*="credit"]'),
        placement: 'left',
      },
      {
        title: '本文生成を開始',
        description:
          '準備ができたら「本文生成を開始」ボタンでSTEP4.2に進みます。AIが各見出しの本文を順番に執筆していきます。',
        target: () =>
          document.querySelector('.start-generation-button') ||
          document.querySelector('button[type="primary"]'),
        placement: 'top',
      },
    ],
  },

  // ===================================================================
  // 生成STEP4.3: ブラッシュアップ
  // ===================================================================
  generation_step4_3: {
    key: 'generation_step4_3',
    steps: [
      {
        target_key: 'intro',
        title: '本文のブラッシュアップ',
        description:
          '生成された記事を改善します。スタイルとの整合性、記事全体の一貫性、表記揺れチェック、読みやすさの改善などを自動で補正します。',
        placement: 'center',
        target: null,
      },
      {
        target_key: 'substeps',
        title: 'サブステップナビゲーション',
        description:
          '現在はSTEP4.3「ブラッシュアップ」です。ブラッシュアップ完了後、STEP4.4「最終調整」に進めます。',
        placement: 'bottom',
        target: () => document.querySelector('.tour-step4-substeps'),
      },
      {
        target_key: 'content_area',
        title: '本文表示エリア',
        description:
          '左側に現在のバージョンの本文が表示されます。ブラッシュアップ後は変更箇所がハイライトされます。',
        placement: 'right',
        target: () => {
          const cols = Array.from(document.querySelectorAll('.ant-col-15'));
          return cols.find(col => col.querySelector('.ant-card'));
        },
      },
      {
        target_key: 'refinement_items',
        title: 'ブラッシュアップ項目',
        description:
          '改善したい項目を選択します。スタイルとの整合性、記事全体の一貫性、表記揺れチェック、読みやすさの改善から選べます。',
        placement: 'left',
        target: () => {
          const cards = Array.from(document.querySelectorAll('.ant-card'));
          return cards.find(card => {
            const title = card.querySelector('.ant-card-head-title');
            return title?.textContent?.includes('ブラッシュアップ項目');
          });
        },
      },
      {
        target_key: 'custom_prompt',
        title: '追加指示（任意）',
        description:
          '特定の改善要望があれば、ここに入力できます。例：「専門用語を減らして初心者向けに」など。',
        placement: 'left',
        target: () => {
          const cards = Array.from(document.querySelectorAll('.ant-card'));
          return cards.find(card => {
            const title = card.querySelector('.ant-card-head-title');
            return title?.textContent?.includes('追加指示');
          });
        },
      },
      {
        target_key: 'execute_button',
        title: 'ブラッシュアップ実行',
        description:
          '選択した項目と追加指示をもとに、AIが記事を改善します。完了までに数分かかります。',
        placement: 'top',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(btn =>
            btn.textContent?.includes('ブラッシュアップを実行'),
          );
        },
      },
      {
        target_key: 'history_card',
        title: 'ブラッシュアップ履歴と回数制限',
        description:
          'フリープランではブラッシュアップ回数は0回です。ランクアップすることで上限が増え、過去のバージョンと比較したり、戻したりできるようになります。',
        placement: 'left',
        target: () => {
          const cards = Array.from(document.querySelectorAll('.ant-card'));
          return cards.find(card => {
            const title = card.querySelector('.ant-card-head-title');
            return title?.textContent?.includes('ブラッシュアップ履歴');
          });
        },
      },
      {
        target_key: 'next_button',
        title: '最終調整へ進む',
        description:
          'ブラッシュアップが完了したら、最後の工程「最終調整」に進みましょう。画像の挿入やエクスポートが可能になります。',
        placement: 'top',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(
            btn =>
              btn.textContent?.includes('最終調整へ進む') ||
              btn.textContent?.includes('次へ'),
          );
        },
      },
    ],
  },

  // ===================================================================
  // 生成STEP4.4: 最終調整
  // ===================================================================
  generation_step4_4: {
    key: 'generation_step4_4',
    steps: [
      {
        target_key: 'intro',
        title: '最終調整',
        description:
          '記事の最終確認と調整を行います。リッチテキストエディタで軽微な修正、画像の挿入、そして原稿の確定とエクスポートができます。',
        placement: 'center',
        target: null,
      },
      {
        target_key: 'substeps',
        title: 'サブステップナビゲーション',
        description:
          '現在はSTEP4.4「最終調整」です。最終調整完了後、生成が完了します。',
        placement: 'bottom',
        target: () => document.querySelector('.tour-step4-substeps'),
      },
      {
        target_key: 'editor_area',
        title: 'リッチテキストエディタ',
        description:
          '左側のエディタで記事を編集できます。見出しの追加、箇条書き、テーブルの作成など、自由に調整可能です。',
        placement: 'right',
        target: () => {
          // Editor.jsのholderを直接探す
          return (
            document.querySelector('#step4-editor') ||
            document.querySelector('.codex-editor') ||
            (() => {
              const cols = Array.from(document.querySelectorAll('.ant-col'));
              return cols.find(col => {
                const classes = col.className || '';
                return (
                  classes.includes('ant-col-16') ||
                  classes.includes('ant-col-lg-16')
                );
              });
            })()
          );
        },
      },
      {
        target_key: 'right_panel_toggle',
        title: '右パネル切り替え',
        description:
          '「画像挿入」で記事に画像を追加、「参考データ」でAIが調べたエビデンスを確認・挿入できます。',
        placement: 'left',
        target: () => {
          const segments = Array.from(
            document.querySelectorAll('.ant-segmented'),
          );
          return segments.find(seg => {
            const text = seg.textContent || '';
            return text.includes('画像挿入') && text.includes('参考データ');
          });
        },
      },
      {
        target_key: 'image_panel',
        title: '画像挿入',
        description:
          '無料の高品質画像を検索し、左側のエディタにドラッグ＆ドロップすると挿入できます。',
        placement: 'left',
        target: () => {
          // 右カラム全体をターゲット（.ant-col-8 or .ant-col-lg-8）
          const cols = Array.from(document.querySelectorAll('.ant-col'));
          const rightCol = cols.find(col => {
            const classes = col.className || '';
            return (
              classes.includes('ant-col-8') || classes.includes('ant-col-lg-8')
            );
          });

          if (rightCol) return rightCol;

          // フォールバック：Segmentedの親
          const segmented = document.querySelector('.ant-segmented');
          if (segmented) {
            return segmented.closest('.ant-col');
          }

          return null;
        },
      },
      {
        target_key: 'auto_save',
        title: '自動保存',
        description:
          '編集内容は自動的に保存されます。左上で保存状況を確認でき、「一時保存」ボタンで手動保存も可能です。',
        placement: 'bottom',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(btn => btn.textContent?.includes('一時保存'));
        },
      },
      {
        target_key: 'citation_toggle',
        title: '引用番号の表示/非表示',
        description:
          '引用番号[1][2]の表示を切り替えられます。エクスポート時にも反映されます。',
        placement: 'bottom',
        target: () => {
          const checkboxes = Array.from(
            document.querySelectorAll('.ant-checkbox-wrapper'),
          );
          return checkboxes.find(cb =>
            cb.textContent?.includes('引用番号を表示'),
          );
        },
      },
      {
        target_key: 'export_buttons',
        title: 'エクスポート',
        description:
          '記事をHTMLでコピーしてWordPress等に貼付したり、Markdown形式でエクスポートできます。エクスポートは生成完了後も可能です。',
        placement: 'bottom',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(
            btn =>
              btn.textContent?.includes('エクスポート') ||
              btn.textContent?.includes('HTML'),
          );
        },
      },
      {
        target_key: 'confirm_button',
        title: '原稿を確定する',
        description:
          '編集が完了したら、このボタンで原稿を確定して「生成完了」ステータスにします。その後は閲覧モードになり、過去のステップも確認できます。',
        placement: 'bottom',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(btn =>
            btn.textContent?.includes('原稿を確定する'),
          );
        },
      },
    ],
  },

  // ===================================================================
  // スタイル管理
  // ===================================================================
  style_management: {
    key: 'style_management',
    steps: [
      {
        // STEP 0
        title: 'スタイル管理へようこそ',
        description:
          'ここでは記事のトーン、文体、構造などのスタイルを管理できます。独自のスタイルを登録して、記事生成時に適用できます。',
        target: null,
      },
      {
        // STEP 1
        title: 'スタイル一覧',
        description:
          '登録済みのスタイルが一覧で表示されます。各スタイルの特徴（文体トーン、専門性、装飾頻度など）を確認できます。',
        target: () => document.querySelector('.ant-table') || document.querySelector('.ant-card'),
        placement: 'bottom',
      },
      {
        // STEP 2
        title: '新規スタイル作成',
        description:
          '「新規作成」ボタンをクリックして、新しいスタイルを作成できます。次のステップでモーダルが開きます。',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(btn => btn.textContent?.includes('新規作成'));
        },
        placement: 'bottom',
        action: 'open_create_modal',
      },
      {
        // STEP 3
        title: '作成方法の選択',
        description:
          'スタイルの作成方法は2つあります：\n\n① URL分析：参考にしたい記事のURLを入力すると、AIがその記事の文体を分析してスタイルを自動生成します。\n\n② 手動設定：すべての項目を自分で設定します。',
        target: () => document.querySelector('.ant-modal-content'),
        placement: 'left',
      },
      {
        // STEP 4
        title: 'URL分析による作成',
        description:
          '参考URLを入力して「分析開始」をクリックすると、AIが記事の文体（トーン、専門性、装飾頻度など）を自動で分析します。\n\n分析結果は後から調整可能です。',
        target: () => {
          // モーダル内の「分析開始」ボタンを探す
          const modal = document.querySelector('.ant-modal-content');
          if (!modal) return null;
          const buttons = Array.from(modal.querySelectorAll('.ant-btn'));
          const analyzeBtn = buttons.find(btn => btn.textContent?.includes('分析開始'));
          return analyzeBtn || modal;
        },
        placement: 'left',
        action: 'close_create_modal',
      },
      {
        // STEP 5
        title: 'スタイルの編集',
        description:
          '作成済みのスタイルは、カードをクリックして詳細設定を変更できます。\n\n次のステップで編集画面を開いて設定項目を確認しましょう。',
        target: () => {
          // スタイルカード（最初のカード）を探す
          const cards = document.querySelectorAll('.ant-card');
          // style-managementクラスを持つ親の中のカードを探す
          for (const card of cards) {
            if (card.classList.contains('ant-card-hoverable')) {
              return card;
            }
          }
          return cards[0] || null;
        },
        placement: 'right',
        action: 'open_edit_modal',
      },
      {
        // STEP 6
        title: 'スタイル設定項目',
        description:
          '左側で各種設定を調整できます：\n\n• 文体トーン（フォーマル〜カジュアル）\n• 専門性レベル（初心者向け〜専門家向け）\n• 見出しスタイル（疑問形、体言止めなど）\n• 装飾頻度（箇条書き、表、引用の使用頻度）\n• 接続詞の使用傾向',
        target: () => {
          // 設定モーダル内の左側（フォーム部分）
          const modal = document.querySelector('.style-modal-fixed .ant-modal-content');
          if (!modal) return null;
          // 左側のCol（md={14}）を探す
          const formCol = modal.querySelector('.ant-col-md-14') || modal.querySelector('.ant-form');
          return formCol || modal;
        },
        placement: 'right',
      },
      {
        // STEP 7
        title: 'プレビューで確認',
        description:
          '右側のプレビューで、設定した内容がリアルタイムに反映されます。\n\n記事生成結果を見ながら微調整していくのがおすすめです。',
        target: () => {
          // 設定モーダル内の右側（プレビュー部分）
          const modal = document.querySelector('.style-modal-fixed .ant-modal-content');
          if (!modal) return null;
          // 右側のCol（md={10}）またはプレビューカードを探す
          const previewCol = modal.querySelector('.ant-col-md-10');
          const previewCard = modal.querySelector('.style-summary-card');
          return previewCard || previewCol || null;
        },
        placement: 'left',
        action: 'close_edit_modal',
      },
      {
        // STEP 8
        title: 'スタイルの活用',
        description:
          '登録したスタイルは、STEP1の「スタイル選択」で選べます。\n\n同じキーワードでもスタイルを変えることで、異なるテイストの記事を生成できます。',
        target: null,
      },
    ],
  },

  // ===================================================================
  // 独自情報管理
  // ===================================================================
  topping_management: {
    key: 'topping_management',
    steps: [
      {
        title: '独自情報管理へようこそ',
        description:
          'ここでは記事生成に使用する独自情報（CSV、PDF、URL、テキスト）を管理できます。自社の商品情報やデータを記事に組み込めます。',
        target: null,
      },
      {
        title: 'ユニット使用状況',
        description:
          '右上に独自情報のユニット使用状況が表示されます。\n\n• URL: 1URL = 1ユニット\n• CSV: 100行 = 1ユニット\n• PDF: 5ページ = 1ユニット\n• TEXT: 5000文字 = 1ユニット\n\nプランに応じた上限があります。',
        target: () => {
          // ユニット表示部分を探す
          const statElements = document.querySelectorAll('.ant-statistic');
          return statElements[0] || null;
        },
        placement: 'left',
      },
      {
        title: '独自情報一覧',
        description:
          '登録済みの独自情報が一覧で表示されます。タイプ、カテゴリ、タグで整理・フィルタリングできます。',
        target: () => document.querySelector('.ant-table') || document.querySelector('.ant-card'),
        placement: 'bottom',
      },
      {
        title: '新規追加',
        description:
          '「新規追加」ボタンをクリックして、独自情報を追加してみましょう。次のステップでウィザードが開きます。',
        target: () => {
          const buttons = Array.from(document.querySelectorAll('.ant-btn'));
          return buttons.find(btn => btn.textContent?.includes('新規追加'));
        },
        placement: 'bottom',
        // モーダルを開くアクション
        action: 'open_create_modal',
      },
      {
        title: 'データタイプの選択',
        description:
          '追加したいデータのタイプを選択します：\n\n• CSV：商品リスト、料金表など\n• PDF：カタログ、マニュアルなど\n• URL：Webページの内容を取り込み\n• TEXT：自由形式のテキスト情報',
        target: () => document.querySelector('.ant-modal-content') || document.querySelector('.ant-drawer-content'),
        placement: 'right',
      },
      {
        title: 'カテゴリ設定',
        description:
          'カテゴリを設定すると、記事生成時に適切な独自情報を見つけやすくなります：\n\n• CTA・行動喚起\n• 商品・サービス情報\n• 事例・実績\n• よくある質問\n• 統計・データ など',
        target: () => document.querySelector('.ant-modal-content') || document.querySelector('.ant-drawer-content'),
        placement: 'right',
        // モーダルを閉じるアクション
        action: 'close_modal',
      },
      {
        title: '記事生成での活用',
        description:
          '登録した独自情報は、STEP3の構成編集画面で「独自情報」タブから選択できます。\n\n各セクションに関連する独自情報を紐づけることで、オリジナリティのある記事を生成できます。',
        target: null,
      },
      {
        title: 'ヒント',
        description:
          '効果的な独自情報の例：\n\n• 自社サービスの特徴・強み\n• 料金プラン・比較表\n• お客様の声・導入事例\n• よくある質問と回答\n• 会社概要・連絡先情報',
        target: null,
      },
    ],
  },

  // ===================================================================
  // マイクロヒント版（初回訪問用: 1-2ステップ + 「詳しくは右上のボタンで」）
  // ===================================================================
  generation_step1_micro: {
    key: 'generation_step1_micro',
    steps: [
      {
        title: 'STEP1: 記事生成の基本設計',
        description:
          'リサーチ結果とスタイルを選択して、記事の方向性を決定します。リサーチ結果がある場合は必ず選択しましょう。',
        target: null,
      },
      {
        title: 'より詳しいガイド',
        description:
          'より詳しい操作ガイドは、右上の ? ボタンからいつでも確認できます。',
        target: null,
      },
    ],
  },
  generation_step2_micro: {
    key: 'generation_step2_micro',
    steps: [
      {
        title: 'STEP2: 構成パターンの選択',
        description:
          'AIが3つの切り口で構成案を生成しました。タブで切り替えて比較し、最適なパターンを選びましょう。',
        target: null,
      },
      {
        title: 'より詳しいガイド',
        description:
          'より詳しい操作ガイドは、右上の ? ボタンからいつでも確認できます。',
        target: null,
      },
    ],
  },
  generation_step3_micro: {
    key: 'generation_step3_micro',
    steps: [
      {
        title: 'STEP3: 構成案の編集',
        description:
          '記事の骨子を固めるステップです。見出しの編集、並び替え、追加ができます。右パネルのトピックも参考にしましょう。',
        target: null,
      },
      {
        title: 'より詳しいガイド',
        description:
          'より詳しい操作ガイドは、右上の ? ボタンからいつでも確認できます。',
        target: null,
      },
    ],
  },
  generation_step4_1_micro: {
    key: 'generation_step4_1_micro',
    steps: [
      {
        title: 'STEP4.1: 執筆プラン',
        description:
          '各セクションの執筆方針を確認・編集できます。左のセクションを選択し、右のフォームで内容を確認してください。そのまま進めることも可能です。',
        target: null,
      },
      {
        title: 'より詳しいガイド',
        description:
          'より詳しい操作ガイドは、右上の ? ボタンからいつでも確認できます。',
        target: null,
      },
    ],
  },
  generation_step4_4_micro: {
    key: 'generation_step4_4_micro',
    steps: [
      {
        title: 'STEP4.4: 最終調整',
        description:
          '生成された記事を確認・編集できます。画像の挿入やテキストの編集が可能です。',
        target: null,
      },
      {
        title: 'より詳しいガイド',
        description:
          'より詳しい操作ガイドは、右上の ? ボタンからいつでも確認できます。',
        target: null,
      },
    ],
  },
};
